/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState, ReactNode } from 'react'
import { useLocation } from 'react-router'
import { PageContext, PageContextValue } from './PageContext'
import { useAuthService } from '../../hooks/useAuthService'
import { useServiceContext } from '../../root'
import { isCheckmated as isCheckmatedCheck } from '../../services/checkmate'

interface PageStateProps {
  children: ReactNode
}

export function PageState({ children }: PageStateProps) {
  const location = useLocation()
  const authService = useAuthService()
  const { api, service } = useServiceContext()

  const [stateName, setStateName] = useState('')
  const [stateParams, setStateParams] = useState<Record<string, any>>({})
  const [url, setUrl] = useState('')
  const [isCheckmated, setIsCheckmated] = useState<boolean | null>(null)
  const [isStagingContextSelected, setIsStagingContextSelected] = useState<boolean | null>(null)
  const [authTypes, setAuthTypes] = useState<{ allowedAuthTypes: string[] }>({ allowedAuthTypes: [] })
  const [featuresInfo, setFeaturesInfo] = useState<any>(null)
  const [unreadNotifCount, setUnreadNotifCount] = useState<number | null>(null)
  const unreadNotifCountRef = useRef<number | null>(null)
  const stagingContextRef = useRef<string | number | null>(null)

  useEffect(() => {
    // Update state name and URL based on current route
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const currentStateName = pathSegments.length > 0 ? pathSegments.join('.') : 'home'

    setStateName(currentStateName)
    setUrl(location.pathname)

    // Parse query parameters
    const searchParams = new URLSearchParams(location.search)
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      params[key] = value
    })
    setStateParams(params)
  }, [location])

  useEffect(() => {
    // Initialize page state data
    initializePageState()

    // Load notification count when user is available
    if (authService.user) {
      getAndUpdateNotificationCount()
    }
  }, [authService.user])

  // Monitor staging context changes
  useEffect(() => {
    const checkStagingContext = () => {
      const currentContextId = service.staging.getContextId()
      const hasContext = !!currentContextId

      // Only update if the staging context actually changed
      if (stagingContextRef.current !== currentContextId) {
        stagingContextRef.current = currentContextId
        setIsStagingContextSelected(hasContext)
      }
    }

    // Check staging context initially
    checkStagingContext()

    // Set up interval to check for staging context changes
    // (Since staging context can change via route navigation, URL params, or programmatic changes)
    const interval = setInterval(checkStagingContext, 1000)

    return () => clearInterval(interval)
  }, [service.staging])

  useEffect(() => {
    unreadNotifCountRef.current = unreadNotifCount
  }, [unreadNotifCount])

  async function initializePageState() {
    try {
      // Load auth types and features info using the converted init service
      const authTypesData = await service.init.loadAllowedAuthTypes()
      setAuthTypes(authTypesData)

      const featuresData = await service.init.loadFeaturesInfo()
      setFeaturesInfo(featuresData)

      setIsCheckmated(isCheckmatedCheck())

      // Initialize staging context selection state
      const initialStagingContext = service.staging.getContextId()
      setIsStagingContextSelected(!!initialStagingContext)
      stagingContextRef.current = initialStagingContext

      setUnreadNotifCount(0)
    } catch (error) {
      console.error('Error initializing page state:', error)

      // Set default values on error
      setAuthTypes({ allowedAuthTypes: [] })
      setFeaturesInfo({})
      setIsCheckmated(false)
      setIsStagingContextSelected(false)
      setUnreadNotifCount(0)
    }
  }

  async function getAndUpdateNotificationCount() {
    try {
      // TODO: Use the notifications API service when available
      // const data = await api.notifications.getCountNotSeen()
      // updateNotificationCount(data?.count || 0)

      // For now, set to 0 until notification service is integrated
      updateNotificationCount(0)
    } catch (error) {
      console.error('Error getting notification count:', error)
      // Set to 0 on error
      updateNotificationCount(0)
    }
  }

  function updateNotificationCount(count: number | null | undefined) {
    if (count === undefined || count === null) {
      console.log('Notification Count is not a number')
    } else {
      if (unreadNotifCountRef.current !== count) {
        setUnreadNotifCount(count)
      }
    }
  }

  const contextValue: PageContextValue = {
    url,
    stateName,
    stateParams,
    user: authService.user || {},
    isCheckmated,
    isStagingContextSelected,
    isStagingUploader: authService.isStagingUploader(),
    isStagingUser: authService.isStagingUser(),
    isRcrUser: authService.isRcrUser(),
    policies: authService.policies || {},
    authTypes,
    featuresInfo,
    unreadNotifCount
  }

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
}
